import { Handle, Position } from 'reactflow';
import { AlertTriangle } from 'lucide-react';
import { resumoDoNo, pendenciaDoNo } from '@shared/flow-node-summary';

/**
 * A moldura comum dos blocos do canvas.
 *
 * Antes cada bloco desenhava a própria caixa e, quando faltava configuração,
 * dizia "Clique para configurar" — a mesma frase em todos, sem dizer o que
 * faltava nem o que o bloco faz. Ler um fluxo de dez passos exigia abrir os
 * dez.
 *
 * Agora o cartão mostra uma linha em português do que o passo faz
 * (`resumoDoNo`) e, quando algo impede o fluxo de rodar, a pendência
 * (`pendenciaDoNo`) — a mesma que aparece no painel "Antes de ativar", para
 * a pessoa não precisar cruzar duas listas.
 *
 * Os `Handle` continuam em cada bloco: são eles que definem de onde sai cada
 * aresta, e centralizá-los aqui mudaria a topologia de fluxos já salvos.
 */

export const LARGURA_PADRAO = 'w-[224px]';

export function EntradaDoNo() {
  return (
    <Handle
      type="target"
      position={Position.Left}
      className="!w-2.5 !h-2.5 !bg-gray-300 !border-gray-400"
    />
  );
}

export function SaidaPadrao(props: { id?: string; style?: React.CSSProperties }) {
  return (
    <Handle
      type="source"
      position={Position.Right}
      id={props.id}
      style={props.style}
      className="!w-2.5 !h-2.5 !bg-gray-300 !border-gray-400"
    />
  );
}

export function CartaoDoNo({
  tipo,
  dados,
  icone: Icone,
  corDoTopo,
  corDoIcone,
  titulo,
  largura = LARGURA_PADRAO,
  resumo,
  children,
}: {
  tipo: string;
  dados: any;
  icone: any;
  /** Classe da faixa colorida do topo, ex: 'bg-green-500'. */
  corDoTopo: string;
  /** Classe do ícone, ex: 'text-green-600'. */
  corDoIcone: string;
  titulo: string;
  largura?: string;
  /** Sobrescreve o resumo automático quando o bloco sabe dizer melhor. */
  resumo?: string;
  children?: React.ReactNode;
}) {
  const texto = resumo ?? resumoDoNo(tipo, dados);
  const pendencia = pendenciaDoNo(tipo, dados);

  return (
    <div
      className={`${largura} bg-white rounded-lg border shadow-sm overflow-visible transition-colors ${
        pendencia ? 'border-amber-300' : 'border-border/60'
      }`}
    >
      <div className={`h-1 ${corDoTopo} rounded-t-lg`} />
      <div className="p-3">
        <div className="flex items-start gap-2">
          <Icone className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${corDoIcone}`} strokeWidth={1.8} />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-foreground truncate">{titulo}</div>
            {texto && (
              <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground line-clamp-3 break-words">
                {texto}
              </p>
            )}
          </div>
        </div>

        {children}

        {pendencia && (
          <div className="mt-2 flex items-center gap-1 text-[10px] font-medium text-amber-700">
            <AlertTriangle className="w-3 h-3 shrink-0" strokeWidth={2} />
            {pendencia.curta}
          </div>
        )}
      </div>
    </div>
  );
}
